import { Fragment, PropsWithChildren, useEffect, useState, Suspense, lazy } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import track from 'c9-tracking';
import HouseholdScreenView from '../views/household/HouseholdScreen.view';
import ReceiptScreenView from '../views/receipt/ReceiptScreen.view';
import { ErrorScreenView } from '../views/error/ErrorScreen.view';
import { useGlobalDispatch, useGlobalState } from '../common/context/AppContext';
import { saveToObjectCache } from '../common/actions/ObjectCacheActions';
import { IAppProps } from '../common/interface/IAppProvider';
import usePreviousRoute from '../common/customHooks/usePreviousRoute';
import MockRouteViewScreen from '../views/mock-route-view/MockRouteViewScreen';

const CustomerOrUserView = lazy(
  () => import(/*webpackChunkName: 'CustomerOrUser' */ '../views/customerUser/customerUserScreen.view')
);
const PolicyHolderView = lazy(
  () => import(/*webpackChunkName: 'PolicyHolder' */ '../views/policyHolder/policyHolderScreen.view')
);
const PackageScreenView = lazy(
  () => import(/*webpackChunkName: 'PackageScreenView' */ '../views/packages/PackageScreen.view')
);
const BasketScreenView = lazy(
  () => import(/*webpackChunkName: 'BasketScreenView' */ '../views/basket/BasketScreen.view')
);
const AboutYouScreen = lazy(
  () => import(/*webpackChunkName: 'AboutYouScreen' */ '../views/about-you/AboutYouScreen.view')
);
const TerminationScreenView = lazy(
  () => import(/*webpackChunkName: 'TerminationScreenView' */ '../views/termination/TerminationScreen.view')
);
const PaymentOptionsScreenView = lazy(
  () => import(/*webpackChunkName: 'PaymentOptionsScreenView' */ '../views/payment-options/PaymentOptionsScreenView')
);
const PaymentFrequencyScreenView = lazy(
  () =>
    import(/*webpackChunkName: 'PaymentFrequencyScreenView' */ '../views/payment-frequency/PaymentFrequencyScreen.view')
);
const ComplianceScreenView = lazy(
  () => import(/*webpackChunkName: 'ComplianceScreenView' */ '../views/compliance/ComplianceScreen.view')
);
const DynamicModalScreenView = lazy(
  () => import(/*webpackChunkName: 'DynamicModalScreenView' */ '../views/dynamic-modal/DynamicModalScreen.view')
);
const PageNotFoundScreenView = lazy(
  () => import(/*webpackChunkName: 'PageNotFoundScreenView' */ '../views/pageNotFound/PageNotFoundScreen.view')
);

interface IModal {
  readonly props: PropsWithChildren<IAppProps>;
  readonly location: any;
  readonly isLoggedOn: boolean;
}

export enum ROUTE_NAMES {
  HOUSEHOLD = '/', // 0
  CUSTOMER_USER = '/kunde-eller-ej', // 1
  POLICY_HOLDER = '/cpr', // 2
  PACKAGES = '/pakker', // 2
  BASKET = '/indkoebskurv', // 3
  ABOUT_YOU = '/om-dig', // 4
  TERMINATION = '/opsigelse', // 5
  PAYMENT_OPTIONS = '/betalingsmaade', // 6
  PAYMENT_FREQUENCY = '/betalingsfrekvens', // 7
  COMPLIANCE = '/accept', // 8
  LOGIN = '/log-ind', // 1
  PAGE_NOT_FOUND = '/404',
  ERROR = '/fejl',
  CALL_ME = '/rmo',
  CALL_ME_RECEIPT = '/kvittering-rmo',
  RECEIPT = '/'
}

export default function RouterApp(props: PropsWithChildren<IAppProps>): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const globalState = useGlobalState();
  const appDispatch = useGlobalDispatch();
  const [error, setError] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(location);
  const prevLocation = usePreviousRoute(location);
  const isReceipt = props.config.baseName.indexOf('kvittering') !== -1;
  const isLoggedOn = window.topContext.user.isLoggedOn;
  const historyPath = location.pathname;
  const pathname = historyPath.slice(-1) === '/' && historyPath.length > 1 ? historyPath.slice(0, -1) : historyPath;
  const index = Object.values(ROUTE_NAMES).findIndex((item) => {
    const regex = new RegExp(item + '/?$');
    return regex.test(pathname);
  });

  useEffect(() => {
    const noTrackList = ['/', '/kvittering', '/pakker', '/pakker/'];
    if (prevLocation !== location && !noTrackList.includes(location.pathname) && !isReceipt) {
      track.navigate(document.URL, `Show ${location.pathname} view to the customer`);
    }
  }, [prevLocation, location, isReceipt]);

  useEffect(() => {
    if (index !== globalState.actualStep && index !== -1) {
      appDispatch({ actualStep: index });
    }
  }, [appDispatch, index, globalState.actualStep]);

  useEffect(() => {
    if (!globalState.hasError.stepOne && !globalState.hasError.stepTwo && error) {
      setError(false);
    }

    if (!globalState.hasAgeError && !error && globalState.hasError.stepOne) {
      setError(true);
      navigate(ROUTE_NAMES.CALL_ME);
      setCurrentLocation((prevState: any) => ({
        ...prevState,
        state: { ...prevState.state, modal: globalState.prevLocation || prevLocation }
      }));
      appDispatch({ prevLocation: globalState.prevLocation || prevLocation, stateChange: true });
    }
  }, [
    error,
    globalState.hasAgeError,
    globalState.hasError,
    globalState.prevLocation,
    prevLocation,
    appDispatch,
    navigate
  ]);

  useEffect(() => {
    if (!globalState.initialState && globalState.stateChange) {
      saveToObjectCache(props, globalState, appDispatch);
      appDispatch({ stateChange: false });
    }
  }, [props, appDispatch, globalState, globalState.stateChange, globalState.initialState]);

  return <ModalSwitch props={props} location={currentLocation} isLoggedOn={isLoggedOn} />;
}

function ModalSwitch(props: PropsWithChildren<IModal>) {
  const state = useGlobalState();
  let location = useLocation();
  const showModal = props.location.state && props.location.state.modal;
  const isReceipt = props.props.config.baseName.indexOf('kvittering') !== -1;

  if (showModal) {
    location = state.hasError.stepOne
      ? { ...location, pathname: ROUTE_NAMES.CALL_ME }
      : { ...location, pathname: ROUTE_NAMES.CALL_ME_RECEIPT };
    location = { ...location, state: props.location.state };
  }

  //User didn't start the flow and trying to jump to the middle he must go to the start
  if (state.initialState && location.pathname !== ROUTE_NAMES.HOUSEHOLD) {
    return <Navigate to={ROUTE_NAMES.HOUSEHOLD} />;
  }

  const fallBack = <Fragment />;
  return (
    <Suspense fallback={fallBack}>
      <Routes location={showModal || location}>
        {isReceipt ? (
          <Route path={ROUTE_NAMES.RECEIPT} element={<ReceiptScreenView {...props.props} />} />
        ) : (
          <Route path={ROUTE_NAMES.HOUSEHOLD} element={<HouseholdScreenView {...props.props} />} />
        )}
        <Route path={`${ROUTE_NAMES.CUSTOMER_USER}`} element={<CustomerOrUserView {...props.props} />} />
        <Route path={`${ROUTE_NAMES.POLICY_HOLDER}`} element={<PolicyHolderView {...props.props} />} />
        <Route path={`${ROUTE_NAMES.BASKET}`} element={<BasketScreenView {...props.props} />} />
        <Route path={`${ROUTE_NAMES.ABOUT_YOU}`} element={<AboutYouScreen {...props.props} />} />
        <Route path={`${ROUTE_NAMES.TERMINATION}`} element={<TerminationScreenView {...props.props} />} />
        <Route
          path={`${ROUTE_NAMES.PACKAGES}`}
          element={
            <>{state.hasAgeError ? <Navigate to={`${ROUTE_NAMES.ERROR}`} /> : <PackageScreenView {...props.props} />}</>
          }
        />
        <Route path={`${ROUTE_NAMES.PAYMENT_OPTIONS}`} element={<PaymentOptionsScreenView {...props.props} />} />
        <Route path={`${ROUTE_NAMES.PAYMENT_FREQUENCY}`} element={<PaymentFrequencyScreenView {...props.props} />} />
        <Route path={`${ROUTE_NAMES.COMPLIANCE}`} element={<ComplianceScreenView {...props.props} />} />
        <Route path={`${ROUTE_NAMES.CALL_ME}`} element={<MockRouteViewScreen />} />
        <Route path={`${ROUTE_NAMES.CALL_ME_RECEIPT}`} element={<MockRouteViewScreen />} />
        <Route path={`${ROUTE_NAMES.ERROR}`} element={<ErrorScreenView />} />
        <Route path={`${ROUTE_NAMES.PAGE_NOT_FOUND}`} element={<PageNotFoundScreenView {...props.props} />} />
        <Route path="*" element={<Navigate to={`${ROUTE_NAMES.PAGE_NOT_FOUND}`} />} />
      </Routes>
      {showModal && (
        <Routes>
          <Route
            path={`${ROUTE_NAMES.CALL_ME}`}
            element={<DynamicModalScreenView {...props.props} location={props.location} />}
          />
          <Route
            path={`${ROUTE_NAMES.CALL_ME_RECEIPT}`}
            element={<DynamicModalScreenView {...props.props} location={props.location} />}
          />
          <Route path="*" element={<DynamicModalScreenView {...props.props} location={props.location} />} />
        </Routes>
      )}
    </Suspense>
  );
}
